import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { circleIcon } from '@progress/kendo-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ClientRoutes } from '../../../../../Constants/ClientRoutingConstants';
import { SvgIcon } from '@progress/kendo-react-common';
import { enMessages } from '../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { useState } from 'react';
import { State, process } from '@progress/kendo-data-query';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { GetEndpointIcon, GetBulkFlowIcon } from "../../../../../Utilities/CustomIcons";
import { GetEndpointMP2Icon } from "../../../../../Utilities/CustomIcons";
import { DeviceType } from "../../../../../Enums/DeviceType";
import { formatSmartDate } from "../../../../Shared/Utilities/DateFormattingUtils";

interface Props {
  healthData: any;
}

export default function EndpointsHealthTable({ healthData }: Props) {
  const navigate = useNavigate();
  const localizationService = useLocalization();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Online':
        return 'text-gbBrand-green-700';
      case 'Unhealthy':
      case 'Offline':
        return 'text-gbBrand-red-500';
      case 'Warning':
        return 'text-gbBrand-yellow-500';
      default:
        return 'text-gbBrand-blue-500';
    }
  };

  const SerialNumberCell = (props: any) => {
    return (
      <td 
        className="cursor-pointer text-blue-600 underline hover:text-blue-800"
        onClick={() => navigate(`${ClientRoutes.HealthDashboardPage}/${props.dataItem.id}`, { 
          state: { 
            deviceData: props.dataItem, 
            deviceType: "endpoint",
            selectedTile: "endpoints"
          } 
        })}
      >
        {props.dataItem.serialNumber}
      </td>
    );
  };

  const ConnectivityCell = (props: any) => {
    return <td>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="large" className={getStatusColor(props.dataItem.connectivityStatus)} />
          <span>{props.dataItem.connectivityStatus}</span>
        </div>
    </td>;
  };

  const LastSyncCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText, tooltipText } = formatSmartDate(
      props.dataItem.lastSync,
      localizationService,
      enMessages
    );
  
    return (
      <td title={tooltipText}>
        {displayText}
      </td>
    );
  };

  const LastGreaseEventCell = (props: any) => {
    const localizationService = useLocalization();
    const { displayText, tooltipText } = formatSmartDate(
      props.dataItem.lastGreaseEvent,
      localizationService,
      enMessages
    );
  
    return (
      <td title={tooltipText}>
        {displayText}
      </td>
    );
  };

  const LastHeartbeatCell = (props: any) => {
    const localizationService = useLocalization();
    const { displayText, tooltipText } = formatSmartDate(
      props.dataItem.lastHeartbeat,
      localizationService,
      enMessages
    );
  
    return (
      <td title={tooltipText}>
        {displayText}
      </td>
    );
  };

  const FirmwareVersionCell = (props: any) => {
    return <td>{props.dataItem.firmwareVersion !== '' ? props.dataItem.firmwareVersion : '-'}</td>;
  };

  const AssetCell = (props: any) => {
    if (props.dataItem.asset) {
      return <td>{props.dataItem.asset}</td>;
    }
    return <td>-</td>;
  };

  const TypeCell = (props: GridCustomCellProps) => {
    let tooltipText = "";
    if (props.dataItem.deviceType === DeviceType.EndpointMP2) {
      tooltipText = `Endpoint MP2`;
    } else if (props.dataItem.deviceType === DeviceType.EndpointLF) {
      tooltipText = `Endpoint LFHF`;
    } else if (props.dataItem.deviceType === DeviceType.EndpointHF) {
      tooltipText = `Endpoint LFHF`;
    } else if (props.dataItem.deviceType === DeviceType.EndpointBF) {
      tooltipText = `Endpoint Bulk Flow`;
    } else if (props.dataItem.deviceType === "Endpoint") {
      tooltipText = `Endpoint`;
    }

    return (
      <td style={{padding: "0px"}}>
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              cursor: "default",
            }}
            title={tooltipText}
          >
            {props.dataItem.deviceType === DeviceType.EndpointMP2 ? (
              GetEndpointMP2Icon(18, 18)
            ) : props.dataItem.deviceType === DeviceType.EndpointLF ? (
              GetEndpointIcon(20, 20)
            ) : props.dataItem.deviceType === DeviceType.EndpointHF ? (  
              GetEndpointIcon(20, 20)
            ) : props.dataItem.deviceType === DeviceType.EndpointBF ? (
              GetBulkFlowIcon(22, 22)
            ) : props.dataItem.deviceType === "Endpoint" ? (
              GetEndpointIcon(20, 20)
            ) : (
              <FontAwesomeIcon icon={faQuestion} size="lg" />
            )}
          </span>
        </Tooltip>
      </td>
    );
  };

  // Initialize data state for virtual scrolling
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 30,
    sort: [{ field: 'connectivityStatus', dir: 'asc' }],
    filter: {
      logic: 'and',
      filters: []
    }
  });

  // Handle data state changes (pagination, sorting, filtering)
  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  // Process the data with current state
  const result = process(healthData, dataState);

  return (
    <>
      <div className="text-xl font-bold p-4">{localizationService.toLanguageString('custom.endpointsHealth', enMessages.custom.endpointsHealth)}</div>
      <Grid
        style={{ height: 'calc(100vh - 345px)', margin: '0 10px 10px 10px' }}
        rowHeight={46}
        data={result.data}
        total={result.total}
        skip={dataState.skip}
        take={dataState.take}
        scrollable="virtual"
        sortable={true}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
      >
        <Column field="deviceType" title=" " cells={{data: TypeCell}} width="45px" filterable={false} />
        <Column field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} cells={{data: SerialNumberCell}} />
        <Column field="connectivityStatus" title={localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)} cells={{data: ConnectivityCell}} />
        <Column field="asset" title={localizationService.toLanguageString('custom.asset', enMessages.custom.asset)} cells={{data: AssetCell}} />
        <Column field="inactivityTimeoutInMinutes" title={localizationService.toLanguageString('custom.inactivityTimeoutInMinutes', enMessages.custom.inactivityTimeoutInMinutes)} />
        <Column field="lastGreaseEvent" title={localizationService.toLanguageString('custom.lastGreaseEvent', enMessages.custom.lastGreaseEvent)} cells={{data: LastGreaseEventCell}} />
        <Column field="lastSync" title={localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)} cells={{data: LastSyncCell}} />
        <Column field="lastHeartbeat" title={localizationService.toLanguageString('custom.lastHeartbeat', enMessages.custom.lastHeartbeat)} cells={{data: LastHeartbeatCell}} />
        <Column field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} cells={{data: FirmwareVersionCell}} />
      </Grid>
    </>
  );
}
